import { Currency } from "@redotech/money/currencies";
import { checkoutOptimizationBillingSchema } from "@redotech/redo-billing-common/checkout-optimization-billing-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { InsertionPosition } from "../widget";
import { FulfillmentCutoffScheduleParser } from "./fulfillment-cutoff";

const postPurchaseUpsellSchema = z.object({
  createOrderOnUpsell: z.boolean().optional(),
});

const progressBarSchema_ = z.object({
  threshold: z.object({
    amount: z.number(),
    currency: createEnum(Currency, { enumName: "Currency" }),
  }),
  ltThresholdText: z.string(),
  gteThresholdText: z.string(),
  progressBarColor: z.string(),
  selectors: z.array(
    z.object({
      selector: z.string(),
      insertPosition: createEnum(InsertionPosition, {
        enumName: "InsertionPosition",
      }),
    }),
  ),
});
export const progressBarSchema: z.ZodType<ProgressBar> = progressBarSchema_;
export interface ProgressBar extends z.infer<typeof progressBarSchema_> {}

const checkoutOptimizationSettingsSchema = z.object({
  enabled: z.boolean(),
  infinityTestingEnabled: z.boolean().optional(),
  preventSplitShipmentsRateAddition: z.boolean().optional(),
  postPurchaseUpsell: postPurchaseUpsellSchema.optional(),
  fulfillmentCutoffSchedule: FulfillmentCutoffScheduleParser.optional(),
  checkoutOptimizationBilling: checkoutOptimizationBillingSchema.optional(),
  ensureDiscountsCombineWithShippingDiscounts: z.boolean().optional(),
  subscriptionsModeEnabled: z.boolean().optional(),
  /**
   * When enabled (and subscriptions mode is on), the rates endpoint skips the
   * invisible rate-name uniqueness character for the first occurrence of each
   * rate name, so rate names stay clean for consumers that bypass the delivery
   * customization function. See `stripCheckoutOptimizationInvisibleIndicators`.
   */
  hideInvisibleRateNameCharsWhenPossible: z.boolean().optional(),
  storefrontProgressBar: progressBarSchema_.optional(),
  useShippingOptionMetafields: z.boolean().optional(),
  isUsingDiscountRules: z.boolean().optional(),
});

export interface CheckoutOptimizationSettings
  extends z.infer<typeof checkoutOptimizationSettingsSchema> {}

export const checkoutOptimizationSettingsDefault: CheckoutOptimizationSettings =
  {
    enabled: false,
    infinityTestingEnabled: false,
    preventSplitShipmentsRateAddition: false,
    ensureDiscountsCombineWithShippingDiscounts: false,
  };

const CheckoutOptimizationAnalyticsAssumptionsZodSchema = z.object({
  annualOrders: z.number().optional(),
});
export interface CheckoutOptimizationAnalyticsAssumptions
  extends z.infer<typeof CheckoutOptimizationAnalyticsAssumptionsZodSchema> {}

export interface CheckoutOptimizationBillingCreator {
  /**
   * Distinguishes this billing creator type from any other string to
   * ensure the creator conforms to the correct string format.
   */
  __checkoutOptimizationBillingCreator: true;
  value: string;
}

export function getBillingSettingsCreator({
  creatorEmail,
}: {
  creatorEmail: string;
}): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `admin:${creatorEmail}`,
  };
}

export function getSystemBillingSettingsCreator(
  label: string,
): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `system:${label}`,
  };
}
